import { useMutation } from '@apollo/client';
import {
  Checkbox,
  DefaultButton,
  DialogType,
  PrimaryButton,
  ProgressIndicator,
  Stack,
} from '@fluentui/react';
import { cutToast } from 'utility/toast';
import { DialogWrapper } from 'common/components/DialogWrapper';
import { FutureDatePicker } from 'common/components/FutureDatePicker';
import { dateFormat } from 'common/utils/dateFormats';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { TravelAuthorization_travelAuthorization } from 'travelAuthorization/TravelPlan/view/__generated__/TravelAuthorization';
import {
  TravelAuthorizationCopy,
  TravelAuthorizationCopyVariables,
} from './__generated__/TravelAuthorizationCopy';
import TRAVEL_AUTHORIZATION_COPY from './TravelAuthorizationCopy.graphql';

interface CopyTravelPlanDialogProps {
  hidden: boolean;
  travelAuthorizationData:
    | TravelAuthorization_travelAuthorization
    | null
    | undefined;
  onDismiss: () => void;
}

export const CopyTravelPlanDialog: React.FC<CopyTravelPlanDialogProps> = ({
  hidden,
  travelAuthorizationData,
  onDismiss,
}) => {
  const history = useHistory();
  const [departureDate, setDepartureDate] = useState<string | undefined>();
  const [returnDate, setReturnDate] = useState<string | undefined>();
  const [departureError, setDepartureError] = useState(false);
  const [returnError, setReturnError] = useState(false);
  const [isAmountIncluded, setIsAmountIncluded] = useState<
    boolean | undefined
  >();

  const [travelAuthorizationCopy, { loading }] = useMutation<
    TravelAuthorizationCopy,
    TravelAuthorizationCopyVariables
  >(TRAVEL_AUTHORIZATION_COPY, { errorPolicy: 'all' });

  const { id } = { ...travelAuthorizationData };

  const onConfirm = async () => {
    if (id) {
      const { data, errors } = await travelAuthorizationCopy({
        variables: {
          input: {
            entityId: id,
            departureDate,
            returnDate,
            isAmountIncluded,
          },
        },
        update: (cache, { data }) => {
          try {
            cache.modify({
              fields: {
                travelAuthorizationSearchFilterTotals(existing) {
                  if (existing?.totalCount1 !== 0) {
                    const controlTotalAmount =
                      Number(existing?.totalAmount1) +
                      Number(
                        data?.travelAuthorizationCopy?.newTravelAuthorization
                          ?.controlTotalAmount
                      );
                    return {
                      ...existing,
                      totalAmount1: controlTotalAmount.toString(),
                      totalCount1: existing?.totalCount1! + 1,
                    };
                  } else {
                    return {
                      ...existing,
                    };
                  }
                },
                travelAuthorizationSearch(existing) {
                  return {
                    ...existing,
                    nodes: [
                      data?.travelAuthorizationCopy?.newTravelAuthorization!,
                      ...existing?.nodes!,
                    ],
                    pageInfo: existing?.pageInfo,
                  };
                },
              },
            });
          } catch (error) {}
        },
      });
      if (!!errors?.length) {
        cutToast.error(errors[0].message);
      } else {
        history.replace(
          `/ta/travel-plan/travel/${data?.travelAuthorizationCopy?.newTravelAuthorization?.id}`
        );
        cutToast.success('Travel plan copy added successfully.');
        onDismiss();
      }
    }
  };

  return (
    <>
      <DialogWrapper
        hidden={hidden}
        onDismiss={onDismiss}
        dialogContentProps={{
          title: 'Create Travel Authorization (Clone)',
          type: DialogType.largeHeader,
        }}
        minWidth={500}
      >
        <Stack
          tokens={{
            childrenGap: 20,
            padding: '10px 0px 10px 0px',
          }}
        >
          <Stack
            horizontal
            tokens={{
              childrenGap: 20,
            }}
          >
            <Stack style={{ flex: 1 }} key={'1'}>
              <FutureDatePicker
                label="Departure Date"
                customPlaceholder="(optional)"
                onSelectDate={(date) => {
                  if (date) {
                    setDepartureDate(dateFormat(date!.toString()));
                  } else setDepartureDate(undefined);
                }}
                onValidationError={(error) => {
                  error ? setDepartureError(true) : setDepartureError(false);
                }}
              />
            </Stack>
            <Stack style={{ flex: 1 }}>
              <FutureDatePicker
                label="Return Date"
                customPlaceholder="(optional)"
                onSelectDate={(date) => {
                  if (date) {
                    setReturnDate(dateFormat(date!.toString()));
                  } else setReturnDate(undefined);
                }}
                onValidationError={(error) => {
                  error ? setReturnError(true) : setReturnError(false);
                }}
              />
            </Stack>
          </Stack>
          <Stack>
            <Checkbox
              label="Include amounts"
              onChange={(_, value) => {
                setIsAmountIncluded(value);
              }}
            />
          </Stack>
        </Stack>

        <Stack
          tokens={{
            padding: '30px 0px 0px 0px',
            childrenGap: 20,
          }}
        >
          {loading && <ProgressIndicator />}
          <Stack
            horizontal
            horizontalAlign="end"
            tokens={{
              childrenGap: 20,
            }}
          >
            <PrimaryButton
              text="Confirm"
              onClick={onConfirm}
              disabled={returnError || departureError}
            />
            <DefaultButton onClick={onDismiss} text="Cancel" />
          </Stack>
        </Stack>
      </DialogWrapper>
    </>
  );
};
